import { addDays } from "date-fns"
import { maxBy } from "lodash"

import { type IStockOutTimesDetails } from "~/models/stockOutTimes"
import { isInactiveStatus } from "~/utils/lifecycleUtils"
import { round } from "~/utils/math"
import { type StockStatus } from "~/utils/outOfStockDatesUtils"
import { largest, sumBy, groupBy, sortByWeekDiff } from "~/utils/tsUtils"

import { differenceInWeeksFloat, getStartOfCurrentWeekUTC, startOfTodayUTC } from "./datetime"
import {
  getForecastBetweenWeeks,
  getForecastsForWeeks,
  getWeekOfHittingThreshold,
} from "~/utils/forecastUtils"
// The stockout engine: turns the projection module's curve and walk into stockout dates and
// suggested orders.
import { calculateStockLevelAtWeek, calculateStockOutWeekDiff } from "./stockProjection/walk"

const MINIMUM_ORDER_PREDICTION = 0.2

function weekCoverBuffer(purchaseIntervalAndLeadtime: number, forecasts: number[], buffer: number) {
  return getForecastBetweenWeeks(
    forecasts,
    purchaseIntervalAndLeadtime,
    purchaseIntervalAndLeadtime + buffer,
  )
}

export function calculateSafetyStockOut(
  bufferType: "serviceLevel" | "weekCover" | "noSafetyStock",
  bufferValue: number,
  forecasts: number[],
  firstStockOutWeek: number | undefined,
  purchaseIntervalAndLeadtime: number,
  minimumStockLevel: number = 0,
) {
  const fullBufferValue =
    bufferType == "weekCover"
      ? weekCoverBuffer(purchaseIntervalAndLeadtime, forecasts, bufferValue) + minimumStockLevel
      : (bufferType === "noSafetyStock" ? 0 : bufferValue) + minimumStockLevel
  if (bufferType == "weekCover") {
    if (firstStockOutWeek === undefined) {
      return {
        date: undefined,
        buffer: fullBufferValue,
      }
    }

    if (!minimumStockLevel) {
      return {
        date: firstStockOutWeek - bufferValue,
        buffer: fullBufferValue,
      }
    }
  }

  if (firstStockOutWeek === undefined) {
    return {
      date: undefined,
      buffer: fullBufferValue,
    }
  }
  const target = getForecastsForWeeks(forecasts, firstStockOutWeek) - fullBufferValue
  if (target >= 0) {
    // your safety stock out date is after the beginning of current week
    const date = getWeekOfHittingThreshold(target, forecasts)
    return {
      // All-zero forecasts make getWeekOfHittingThreshold extrapolate 0/0 → NaN, which becomes
      // an Invalid Date downstream and poisons date comparisons (e.g. the grouped suggested-order
      // arrival). No forecast means no meaningful safety stockout — report none.
      date: Number.isNaN(date) ? undefined : date,
      buffer: fullBufferValue,
    }
  } else {
    // Your safety stock out date is before beginning of current week
    if (forecastsIsAllZero(forecasts)) {
      return {
        date: firstStockOutWeek,
        buffer: fullBufferValue,
      }
    }
    return {
      date: target / ((forecasts[forecasts.length - 1] ?? 0) / forecasts.length),
      buffer: fullBufferValue,
    }
  }
}

const forecastsIsAllZero = (forecasts: number[]) => forecasts[forecasts.length - 1] === 0

// undefined only when no entry has sales data, so aggregate and per-warehouse fall back together.
const aggregateSalesWeekToDate = (entries: { salesWeekToDate?: number | null }[]) =>
  entries.some((e) => e.salesWeekToDate != null)
    ? entries.reduce((acc, e) => acc + (e.salesWeekToDate ?? 0), 0)
    : undefined

/** `[stock-out week, restock week]` pairs; the restock is missing when nothing refills the gap. */
export type StockOutPeriods = [number, number | undefined][]

// Base result interface for GetStockoutTimesV2 without added Suggested Order metrics
interface StockOutBaseResult {
  variantId: string
  warehouseId: string
  stockoutDates: Date[]
  safetyStockOutDate: Date | undefined
  buffer: number
  status: StockStatus
  suggestedOrder: number
  stockLevelAtArrival: number
  stockOutPeriods: StockOutPeriods
  weeksOnHand: number | undefined
}

// Extended result interface for GetStockoutTimesV2 with added Suggested Order metrics, plus the
// week-space view of the simulation. Kept out of the base result because `stockOutTimesV2` ships
// that one to the client for the whole catalogue.
interface StockOutExtendedResult extends StockOutBaseResult {
  projectedUnderstock: number
  missingStockEndOfPeriod: number
  purchasePeriodLostSales: number
  /**
   * The same stock-out times as `stockoutDates`, in weeks from the start of the current week and
   * without the whole-day truncation `stockoutDates` picks up from `convertFromWeeks`. Metrics that
   * compare a stock-out against the arrival date must use these together with `arrivalWeek`: a
   * run-out hours after the order lands truncates onto the arrival date itself, and a date
   * comparison then reads it as "before arrival" and reports a later replenishment's runway
   * instead (HAK-2359).
   */
  stockoutWeeks: number[]
  /**
   * The week the order lands, in the same scale as `stockoutWeeks`, exactly as the simulation
   * anchored it. Deriving it a second time (`leadtime + todayShift`) lands one ULP off the incoming
   * order's own week for ~13% of weekday/lead-time pairs, which drops the planned order from
   * `stockLevelAtArrival`.
   */
  arrivalWeek: number
}

export type StockOutResult = StockOutBaseResult | StockOutExtendedResult

export function isExtendedStockOutResult(result: StockOutResult): result is StockOutExtendedResult {
  return "projectedUnderstock" in result
}

// We are using 3 overload functions here with separate return interfaces to reduce network load when extended Suggested Order metrics are not needed
export function calculateStockOutAndSafetyStockAndPurchaseOrders(
  variantId: string,
  warehouseId: string,
  buffer: {
    type: "serviceLevel" | "weekCover" | "noSafetyStock"
    value: number
  },
  stockLevel: number,
  incomingOrders: { weekDiff: number; quantity: number }[],
  forecasts: number[],
  todayShift: number,
  leadtime: number,
  purchaseInterval: number,
  overstockThresholdWeeks: number,
  convertFromWeeks: (week: number) => Date,
  excludeSuggestion?: boolean,
  historicStockWeek?: number, // this variable indicates the last day of stock, so it can be used to adjust the stockout date
  minimumStockLevel?: number,
  includeExtendedMetrics?: true,
  ignoreLostSales?: boolean,
  salesWeekToDate?: number,
  arrivalWeekOverride?: number,
): StockOutExtendedResult
export function calculateStockOutAndSafetyStockAndPurchaseOrders(
  variantId: string,
  warehouseId: string,
  buffer: {
    type: "serviceLevel" | "weekCover" | "noSafetyStock"
    value: number
  },
  stockLevel: number,
  incomingOrders: { weekDiff: number; quantity: number }[],
  forecasts: number[],
  todayShift: number,
  leadtime: number,
  purchaseInterval: number,
  overstockThresholdWeeks: number,
  convertFromWeeks: (week: number) => Date,
  excludeSuggestion?: boolean,
  historicStockWeek?: number,
  minimumStockLevel?: number,
  includeExtendedMetrics?: false,
  ignoreLostSales?: boolean,
  salesWeekToDate?: number,
  arrivalWeekOverride?: number,
): StockOutBaseResult
export function calculateStockOutAndSafetyStockAndPurchaseOrders(
  variantId: string,
  warehouseId: string,
  buffer: {
    type: "serviceLevel" | "weekCover" | "noSafetyStock"
    value: number
  },
  stockLevel: number,
  incomingOrders: { weekDiff: number; quantity: number }[],
  forecasts: number[],
  todayShift: number,
  leadtime: number,
  purchaseInterval: number,
  overstockThresholdWeeks: number,
  convertFromWeeks: (week: number) => Date,
  excludeSuggestion?: boolean,
  historicStockWeek?: number,
  minimumStockLevel?: number,
  includeExtendedMetrics?: boolean,
  ignoreLostSales?: boolean,
  salesWeekToDate?: number,
  arrivalWeekOverride?: number,
): StockOutBaseResult | StockOutExtendedResult
export function calculateStockOutAndSafetyStockAndPurchaseOrders(
  variantId: string,
  warehouseId: string,
  buffer: {
    type: "serviceLevel" | "weekCover" | "noSafetyStock"
    value: number
  },
  stockLevel: number,
  incomingOrders: { weekDiff: number; quantity: number }[],
  forecasts: number[],
  todayShift: number,
  leadtime: number,
  purchaseInterval: number,
  overstockThresholdWeeks: number,
  convertFromWeeks: (week: number) => Date,
  excludeSuggestion?: boolean,
  historicStockWeek?: number,
  minimumStockLevel: number = 0,
  includeExtendedMetrics: boolean = false,
  ignoreLostSales: boolean = false,
  salesWeekToDate?: number,
  /**
   * The arrival week, measured off the same week start the incoming orders' `weekDiff` values are.
   * Callers that know the expected arrival date MUST pass it: `leadtime + todayShift` is the same
   * quantity in exact arithmetic but lands one ULP away in floating point, which drops an order
   * arriving exactly at the arrival date. Falls back to `leadtime + todayShift` when the caller only
   * has a lead time.
   */
  arrivalWeekOverride?: number,
): StockOutBaseResult | StockOutExtendedResult {
  // nextOrders[0] is read in array order by calculateStockLevelAtWeek, and by the restock lookup
  // in calculateAvailabilityHelper, so the merged stream has to be chronological.
  const orders = sortByWeekDiff(incomingOrders)

  const stockoutWeeks = calculateStockOutWeekDiff(
    forecasts,
    stockLevel,
    orders,
    todayShift,
    salesWeekToDate,
  )
  // Only replace with historic date if the calculated stockout is at or before today.
  // When there are incoming orders that replenish stock, the first stockout may be in the future
  // (after the incoming stock runs out), and we shouldn't replace that with the historic date.
  if (
    stockLevel === 0 &&
    historicStockWeek !== undefined &&
    (stockoutWeeks[0] ?? 0) <= todayShift
  ) {
    stockoutWeeks[0] = historicStockWeek
  }

  const safetyStockOutput = calculateSafetyStockOut(
    buffer.type,
    buffer.value,
    forecasts,
    stockoutWeeks[0],
    leadtime + purchaseInterval,
    minimumStockLevel,
  )
  const arrivalWeek = arrivalWeekOverride ?? leadtime + todayShift
  const stockLevelAtArrival = calculateStockLevelAtWeek(
    arrivalWeek,
    forecasts,
    orders,
    stockLevel,
    stockoutWeeks,
    todayShift,
    salesWeekToDate,
  )

  const [suggestedOrder, projectedUnderstock, missingStockEndOfPeriod, purchasePeriodLostSales] =
    calculateSuggestedOrder(
      arrivalWeek,
      purchaseInterval,
      stockLevelAtArrival,
      safetyStockOutput.buffer,
      orders,
      forecasts,
      ignoreLostSales,
    )

  const weeksOnHandRaw = forecastsIsAllZero(forecasts)
    ? undefined
    : orders.length === 0
      ? stockoutWeeks[0]
      : getWeekOfHittingThreshold(stockLevel, forecasts)
  const shouldReorder =
    suggestedOrder > 0 &&
    safetyStockOutput.date !== undefined &&
    safetyStockOutput.date < arrivalWeek &&
    !excludeSuggestion

  const base: StockOutBaseResult = {
    variantId,
    warehouseId,
    stockoutDates: stockoutWeeks.map(convertFromWeeks),
    safetyStockOutDate:
      safetyStockOutput.date !== undefined ? convertFromWeeks(safetyStockOutput.date) : undefined,
    buffer: safetyStockOutput.buffer,
    status: calculateStockStatus(stockLevel, shouldReorder, forecasts, overstockThresholdWeeks),
    suggestedOrder: excludeSuggestion ? 0 : suggestedOrder,
    stockLevelAtArrival,
    stockOutPeriods: calculateAvailabilityHelper(stockoutWeeks, orders),
    weeksOnHand: weeksOnHandRaw === undefined ? undefined : Math.round(largest(weeksOnHandRaw, 0)),
  }

  if (!includeExtendedMetrics) return base

  return {
    ...base,
    projectedUnderstock: excludeSuggestion ? 0 : projectedUnderstock,
    missingStockEndOfPeriod: excludeSuggestion ? 0 : missingStockEndOfPeriod,
    purchasePeriodLostSales: excludeSuggestion ? 0 : purchasePeriodLostSales,
    stockoutWeeks,
    arrivalWeek,
  }
}

function calculateStockStatus(
  stocklevel: number,
  shouldReorderNow: boolean,
  forecasts: number[],
  overstockThresholdWeeks: number,
): StockStatus {
  if (forecasts[forecasts.length - 1] === 0) {
    return stocklevel ? "overstock" : "fine"
  }

  if (stocklevel === 0) {
    return "stockout"
  } else if (shouldReorderNow) {
    return "reorder"
  } else if (getForecastsForWeeks(forecasts, overstockThresholdWeeks) < stocklevel) {
    return "overstock"
  }

  return "fine"
}

function calculateStockOutAndSafetyStockAggregated(
  buffers: {
    type: "serviceLevel" | "weekCover" | "noSafetyStock"
    value: number
    minimumStockLevel: number
  }[],
  forecasts: number[][],
  stocklevelSum: number,
  incomingOrders: { weekDiff: number; quantity: number }[],
  todayShift: number,
  leadtime: number,
  purchaseInterval: number,
  overstockThresholdWeeks: number,
  convertFromWeeks: (week: number) => Date,
  variantId: string,
  excludeSuggestions?: boolean,
  historicStockWeek?: number,
  salesWeekToDate?: number,
  arrivalWeekOverride?: number,
) {
  const newForecasts = [...(forecasts[0] ?? [])]
  forecasts.slice(1).forEach((v) => {
    for (let i = 0; i < v.length; i++) {
      newForecasts[i] = (v[i] ?? 0) + (newForecasts[i] ?? 0)
    }
  })
  const ordersAggregated =
    incomingOrders.length > 1
      ? sortByWeekDiff(
          Object.values(groupBy(incomingOrders, "weekDiff")).map((orders) => ({
            quantity: sumBy(orders, "quantity"),
            weekDiff: orders[0]?.weekDiff ?? 0,
          })),
        )
      : incomingOrders
  const buffer =
    buffers[0]?.type == "weekCover" // TODO when enabling differentiated weekcovers on warehouse level we need to change this
      ? buffers[0]
      : { type: "serviceLevel" as const, value: sumBy(buffers, "value") }

  return {
    ...calculateStockOutAndSafetyStockAndPurchaseOrders(
      variantId,
      "",
      buffer,
      stocklevelSum,
      ordersAggregated,
      newForecasts,
      todayShift,
      leadtime,
      purchaseInterval,
      overstockThresholdWeeks,
      convertFromWeeks,
      excludeSuggestions,
      historicStockWeek,
      sumBy(buffers, "minimumStockLevel"),
      undefined,
      undefined,
      salesWeekToDate,
      arrivalWeekOverride,
    ),
    suggestedOrder: undefined, // Suggested order needs to be calucated on warehouse level
  }
}

export function calculateSuggestedOrder(
  /**
   * The week the order lands, measured off the same week start as the incoming orders' `weekDiff`.
   * Taken as-is rather than rebuilt from a lead time so an order arriving exactly at the arrival
   * date falls on the intended side of the `weekDiff > arrivalWeek` window below.
   */
  arrivalWeek: number,
  purchaseInterval: number,
  stockLevelAtArrival: number,
  buffer: number,
  incomingOrders: { weekDiff: number; quantity: number }[],
  forecasts: number[],
  ignoreLostSales: boolean,
): [number, number, number, number] {
  if (!forecasts[forecasts.length - 1]) {
    // If we forecast 0 no matter what we need make the suggested order 0
    return [0, 0, 0, 0]
  }
  const nextArrivalWeek = arrivalWeek + purchaseInterval

  // Incoming orders within the purchase period, sorted chronologically.
  // Use <= nextArrivalWeek so orders landing exactly on the period boundary are included.
  const periodOrders = sortByWeekDiff(
    incomingOrders.filter(
      (order) => order.weekDiff > arrivalWeek && order.weekDiff <= nextArrivalWeek,
    ),
  )
  const incomingOrdersTotal = sumBy(periodOrders, "quantity")

  const demand = getForecastBetweenWeeks(forecasts, arrivalWeek, nextArrivalWeek)

  // With no mid-period orders, no lost sales to account for
  let purchasePeriodLostSales = 0
  if (periodOrders.length > 0 && !ignoreLostSales) {
    // Lost sales = total demand during stockout windows within the purchase period.
    // Detected by running calculateStockOutWeekDiff anchored at the arrival date.
    purchasePeriodLostSales = calculateAvailabilityHelper(
      calculateStockOutWeekDiff(forecasts, stockLevelAtArrival, periodOrders, arrivalWeek),
      periodOrders,
    ).reduce(
      (acc, [start, end]) =>
        acc +
        getForecastBetweenWeeks(
          forecasts,
          Math.max(start, arrivalWeek),
          end !== undefined ? Math.min(end, nextArrivalWeek) : nextArrivalWeek,
        ),
      0,
    )
  }

  const missingStockEndOfPeriod = buffer - (stockLevelAtArrival + incomingOrdersTotal - demand)
  const suggestedOrder =
    purchasePeriodLostSales > 0
      ? Math.max(purchasePeriodLostSales, missingStockEndOfPeriod)
      : missingStockEndOfPeriod

  return [
    // Actual order suggestion, ceiled and clamped to 0, cannot be negative
    MINIMUM_ORDER_PREDICTION > suggestedOrder ? 0 : Math.ceil(suggestedOrder),
    // Raw suggested order, can be negative in cases of overstock and no lost sales, rounded to 2 decimals
    round(suggestedOrder, 3),
    // Missing stock end of period, raw expression of what the projected stock level is like at the end of period
    // Does not account for lost sales
    round(missingStockEndOfPeriod, 3),
    // Calculated lost sales inbetween incoming orders. Will always be 0 if no incoming orders
    MINIMUM_ORDER_PREDICTION > purchasePeriodLostSales ? 0 : Math.ceil(purchasePeriodLostSales),
  ]
}

function calculateAvailabilityHelper(
  stockOutDates: number[],
  incomingOrders: { weekDiff: number; quantity: number }[],
): StockOutPeriods {
  // Produces an array of time stamps of stockout and restock
  // Even indexes are stockout times and odds indexes are restock times
  // With them it is examply fast to calculate availabilities
  return stockOutDates.map((d, index) => {
    const restock =
      incomingOrders.find((i) => i.weekDiff >= d)?.weekDiff ?? stockOutDates[index + 1]
    return [round(d, 2), restock !== undefined ? round(restock, 2) : undefined]
  })
}

/*
After a quick performance test for one of our heavier customers
we found out that this function takes around 1-2 seconds of threadblocking processing during initial app load
This is a heavy and dangerous place to edit, future developers should be wary of performance implications here
*/
export function getStockOutTimesV2(
  overstockThresholdDays: number | undefined,
  stockOutTimesDetails: IStockOutTimesDetails,
  expectedArrivalDateOfOrder: Date | undefined = undefined,
  nextOrderArrivalDate: Date | undefined = undefined,
  incomingOrders:
    | { skuId: string; warehouseId: string; quantity: number; expectedArrivalDate: Date }[]
    | undefined = undefined,
  expectedArrivalDateBySku: Map<string, Date> | undefined = undefined,
  excludeFashion: boolean | undefined = false,
  includeExtendedMetrics: boolean = false,
  nextOrderArrivalDateBySku: Map<string, Date> | undefined = undefined,
  // When true, the lost-sales term is dropped from the suggested-order formula. Callers resolve
  // this from fashion context (NOOS always passes false). Defaults off (NOOS-safe).
  ignoreLostSales: boolean = false,
) {
  const data = stockOutTimesDetails

  const thresholdWeeks = (overstockThresholdDays ?? 182) / 7
  const day = getStartOfCurrentWeekUTC()
  const convertFromWeeks = (i: number) => addDays(day, i * 7)
  const todayShift = differenceInWeeksFloat(day, startOfTodayUTC())

  const incomingOrdersMap = incomingOrders
    ? new Map(
        Object.entries(
          groupBy(incomingOrders, (order) => `${order.skuId}///${order.warehouseId}`),
        ).map(([key, entries]) => [
          key,
          entries.map((io) => ({
            weekDiff: differenceInWeeksFloat(day, io.expectedArrivalDate),
            quantity: io.quantity,
          })),
        ]),
      )
    : undefined

  return new Map(
    Object.values(groupBy(data, "skuId")).map((entries) => {
      const skuId = entries[0]?.skuId ?? ""
      const skuEAD = expectedArrivalDateBySku?.get(skuId) ?? expectedArrivalDateOfOrder
      const skuNextOrderArrival = nextOrderArrivalDateBySku?.get(skuId) ?? nextOrderArrivalDate

      const skuLeadtimeWeek = skuEAD ? differenceInWeeksFloat(startOfTodayUTC(), skuEAD) : undefined
      // Anchored on the same week start as the incoming orders' `weekDiff`, so an order landing on
      // the arrival date is counted as on hand there (`leadtime + todayShift` is the same quantity
      // but a floating-point sum, and lands one ULP off for ~13% of weekday/lead-time pairs).
      const skuArrivalWeek = skuEAD ? differenceInWeeksFloat(day, skuEAD) : undefined

      const skuPurchaseIntervalWeek =
        skuNextOrderArrival && skuEAD
          ? differenceInWeeksFloat(skuEAD, skuNextOrderArrival)
          : undefined

      const stockOutInfoWarehouse = Object.fromEntries(
        entries.map((entry) => {
          const orders = [
            ...(entry.incomingOrders ?? []),
            ...(entry.forecastedReturns ?? []),
            ...(incomingOrdersMap?.get(`${entry.skuId}///${entry.warehouseId}`) ?? []),
          ]

          return [
            entry.warehouseId,
            calculateStockOutAndSafetyStockAndPurchaseOrders(
              entry.variantId,
              entry.warehouseId,
              {
                type: entry.bufferType,
                value: entry.bufferValueEffective ?? 0,
              },
              entry.stockLevel ?? 0,
              orders,
              entry.forecasts,
              todayShift,
              skuLeadtimeWeek ?? entry.leadtime / 7,
              skuPurchaseIntervalWeek ?? entry.purchaseInterval / 7,
              thresholdWeeks,
              convertFromWeeks,
              (excludeFashion && entry.productType === "fashion") ||
                isInactiveStatus(entry.lifecycleStatus),
              (entry.stockDay ?? 0) / 7,
              entry.minimumStockLevel ?? 0,
              includeExtendedMetrics,
              ignoreLostSales,
              entry.salesWeekToDate ?? undefined,
              skuArrivalWeek,
            ),
          ]
        }),
      )

      return [
        skuId,
        {
          details: stockOutInfoWarehouse,
          aggregated:
            stockOutInfoWarehouse[entries.length === 1 ? (entries[0]?.warehouseId ?? "") : ""] ??
            calculateStockOutAndSafetyStockAggregated(
              entries.map((e) => ({
                type: e.bufferType,
                value: e.bufferValueEffective ?? 0,
                minimumStockLevel: e.minimumStockLevel ?? 0,
              })),
              entries.map((s) => s.forecasts),
              sumBy(entries, "stockLevel"),
              entries.flatMap((s) => s.incomingOrders ?? []), // TODO THIS DOES NOT INCLUDE IMPUTED INCOMINGS ORDERS. WE SHOULD NEVER USE THIS DATA ON AGGREGATED LEVEL ANY WAY
              todayShift,
              skuLeadtimeWeek ?? (entries[0]?.leadtime ?? 0) / 7,
              skuPurchaseIntervalWeek ?? (entries[0]?.purchaseInterval ?? 0) / 7,
              thresholdWeeks,
              convertFromWeeks,
              entries[0]?.variantId ?? "",
              (excludeFashion && entries[0]?.productType === "fashion") ||
                isInactiveStatus(entries[0]?.lifecycleStatus),
              (maxBy(entries, "stockDay")?.stockDay ?? 0) / 7,
              aggregateSalesWeekToDate(entries),
              skuArrivalWeek,
            ),
        },
      ]
    }),
  )
}
